import { http } from './http'

export type OrdersQuery = {
  page?: number
  pageSize?: number
  search?: string
  status?: string
  processing_types?: string
  excluded_types?: string
  sort_by?: string
}

export async function getOrders(params: OrdersQuery) {
  const res = await http.get('/orders', { params })
  return res.data
}

export async function getOrder(orderId: string) {
  const res = await http.get(`/orders/${orderId}`)
  return res.data
}

export async function postOrderAction(orderId: string, action: string, payload?: any) {
  const res = await http.post(`/orders/${orderId}/${action}`, payload)
  return res.data
}

export async function markViewed(orderId: string) { return postOrderAction(orderId, 'mark-viewed') }
export async function takeOrder(orderId: string) { return postOrderAction(orderId, 'take') }
export async function submitPrice(orderId: string, price: number, comment?: string, delivery_time?: number) { 
  return postOrderAction(orderId, 'price', { price, comment, delivery_time }) 
}
export async function rejectOrder(orderId: string, reason: string) { return postOrderAction(orderId, 'reject', { reason }) }
export async function restoreOrder(orderId: string) { return postOrderAction(orderId, 'restore') }
export async function executeOrder(orderId: string) { return postOrderAction(orderId, 'execute') }
export async function confirmOrder(orderId: string) { return postOrderAction(orderId, 'confirm') }
export async function declineOrder(orderId: string, reason: string) { return postOrderAction(orderId, 'decline', { reason }) }

export async function authWithInitData(initData: string, debug: boolean = false) {
  const res = await http.post('/auth', { initData, debug })
  return res.data as { success: boolean; token?: string; user?: any; message?: string; needs_registration?: boolean; pending_approval?: boolean }
}

export async function registerTelegramUser(data: {
  initData: string
  name: string
  surname: string
  email: string
  password: string
  company_name?: string
  phone?: string
  company_iin?: string
  company_email?: string
}) {
  const res = await http.post('/register', data)
  return res.data as { success: boolean; token?: string; user?: any; message?: string }
}

export async function getChatMessages(orderId: string) {
  const res = await http.get(`/orders/${orderId}/chat`)
  return res.data as { messages: any[] }
}

export async function sendChatMessage(orderId: string, message: string, recipient: 'technologist' | 'manager' | string) {
  const res = await http.post(`/orders/${orderId}/chat`, { message, recipient })
  return res.data as { success: boolean }
}

export async function getUnreadCount(orderId: string) {
  const res = await http.get(`/orders/${orderId}/chat/unread-count`)
  return res.data as { unread_count: number }
}

export async function markChatRead(orderId: string, last_message_id?: string) {
  const res = await http.post(`/orders/${orderId}/chat/mark-read`, last_message_id ? { last_message_id } : {})
  return res.data as { success: boolean }
}

export async function uploadChatFile(orderId: string, file: File, recipient: string, message?: string) {
  const form = new FormData()
  form.append('file', file)
  form.append('recipient', recipient)
  form.append('message', message || '')
  const res = await http.post(`/orders/${orderId}/chat/upload`, form)
  return res.data as { success: boolean }
}

export async function uploadMultipleChatFiles(orderId: string, files: File[], recipient: string, message?: string) {
  const form = new FormData()
  files.forEach(f => form.append('files', f))
  form.append('recipient', recipient)
  form.append('message', message || '')
  const res = await http.post(`/orders/${orderId}/chat/upload-multiple`, form)
  return res.data as { success: boolean }
}

// ===== Files: preview/share/email/download =====
export async function shareSingleOrderFile(orderId: string, filename: string) {
  const form = new FormData()
  form.append('filename', filename)
  const res = await http.post(`/files/share-single-file/${orderId}`, form)
  return res.data as { success: boolean; url: string }
}

export async function shareAllOrderFiles(orderId: string) {
  const res = await http.post(`/files/share-files/${orderId}`)
  return res.data as { success: boolean; urls: string[] }
}

export async function sendSingleOrderFileEmail(orderId: string, recipient_email: string, filename: string, comment?: string) {
  const form = new FormData()
  form.append('recipient_email', recipient_email)
  form.append('filename', filename)
  if (comment) form.append('comment', comment)
  const res = await http.post(`/files/send-single-file-email/${orderId}`, form)
  return res.data as { success: boolean; attached_files: Array<{ name: string }> }
}

export async function sendAllOrderFilesEmail(orderId: string, recipient_email: string, comment?: string) {
  const form = new FormData()
  form.append('recipient_email', recipient_email)
  if (comment) form.append('comment', comment)
  const res = await http.post(`/files/send-files-email/${orderId}`, form)
  return res.data as { success: boolean; attached_files: Array<{ name: string }>; failed_files: Array<{ name: string }> }
}

export async function downloadOrderFile(orderId: string, filename: string) {
  // Используем axios для передачи заголовков авторизации и скачивания blob
  const res = await http.get(`/files/download-file/${orderId}/${encodeURIComponent(filename)}`, { responseType: 'blob' })
  return res.data as Blob
}
